import { Check, Copy, ExternalLink, Headphones, QrCode } from "lucide-react";
import { useEffect, useState } from "react";
import { languageName, type Language } from "@/lib/languages.ts";
import { listenerUrl } from "@/lib/urls.ts";

type Props = {
  code: string | null;
  languages: Language[];
  /** Languages attendees may choose, and those that can be heard as speech. */
  listenerLanguages: string[];
  voices: string[];
};

export function SharePanel({ code, languages, listenerLanguages, voices }: Props) {
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // "" lets each attendee choose on their phone; a language makes a QR that opens straight in it.
  const [qrLang, setQrLang] = useState("");
  const base = code ? listenerUrl(code) : "";
  const url = base && qrLang ? `${base}?lang=${qrLang}` : base;
  const spoken = listenerLanguages.filter((l) => voices.includes(l));

  useEffect(() => {
    if (!url) {
      setQr(null);
      return;
    }
    let cancelled = false;
    void import("qrcode").then((QR) =>
      QR.toDataURL(url, { margin: 1, width: 320, color: { dark: "#14120e", light: "#fffdf8" } }).then((data) => {
        if (!cancelled) setQr(data);
      }),
    );
    return () => {
      cancelled = true;
    };
  }, [url]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked */
    }
  };

  const names = (codes: string[]) => codes.map((c) => languageName(languages, c)).join(", ");

  return (
    <section className="card p-4" aria-labelledby="share-title">
      <h2 id="share-title" className="flex items-center gap-2 text-sm font-medium">
        <QrCode size={16} className="text-accent" /> Share with attendees
      </h2>
      {code ? (
        <>
          <div className="mt-3 flex gap-4">
            {qr ? (
              <img src={qr} alt={`QR code for ${url}`} width={112} height={112} className="h-28 w-28 shrink-0 rounded-lg" />
            ) : (
              <div className="h-28 w-28 shrink-0 rounded-lg bg-surface-2" />
            )}
            <div className="flex min-w-0 flex-col gap-2">
              <p className="font-mono text-lg tracking-[0.2em]">{code}</p>
              <p className="text-xs text-muted">
                {spoken.length
                  ? "Attendees choose their language and tap Listen to hear the translation, or just read it."
                  : "Attendees read the translation live on their phones."}{" "}
                Your microphone audio is never sent to them. The link stops working when the session ends.
              </p>
              <div className="mt-auto flex flex-wrap gap-2">
                <button type="button" className="btn min-h-9 px-3" onClick={() => void copy()}>
                  {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy link"}
                </button>
                <a className="btn min-h-9 px-3" href={url} target="_blank" rel="noreferrer">
                  <ExternalLink size={14} /> Open
                </a>
              </div>
            </div>
          </div>
          {listenerLanguages.length ? (
            <label className="mt-3 flex items-center justify-between gap-2 text-xs text-muted">
              QR code opens in
              <select className="field min-h-9 text-sm text-fg" value={qrLang} onChange={(e) => setQrLang(e.target.value)}>
                <option value="">Attendee's choice</option>
                {listenerLanguages.map((l) => (
                  <option key={l} value={l}>
                    {languageName(languages, l)}
                    {voices.includes(l) ? "" : " (text only)"}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </>
      ) : (
        <p className="mt-2 text-sm text-muted">
          Start a session to get a link and QR code. Attendees follow on their phones
          {spoken.length ? " and can listen to the translation" : ""}.
        </p>
      )}
      {spoken.length ? (
        <p className="mt-3 flex items-start gap-2 text-xs text-faint">
          <Headphones size={14} className="mt-0.5 shrink-0" />
          <span>Spoken: {names(spoken)}.</span>
        </p>
      ) : null}
    </section>
  );
}
